import { useEffect, useState, useRef } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Upload, Download, Trash2, FileText, Filter } from "lucide-react";

const CATEGORIES = ["Vertrag", "Lohnabrechnung", "Bescheinigung", "Zeugnis", "Sonstiges"];

function fmtSize(n) {
  if (!n) return "—";
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / 1024 / 1024).toFixed(2)} MB`;
}

export default function DocumentsPanel({ userId, canUpload = true, canDelete = false }) {
  const [docs, setDocs] = useState([]);
  const [category, setCategory] = useState("Vertrag");
  const [desc, setDesc] = useState("");
  const [filterCat, setFilterCat] = useState("all");
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);

  const load = async () => {
    const { data } = await api.get("/documents", { params: { user_id: userId } });
    setDocs(data);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [userId]);

  const upload = async () => {
    const file = fileRef.current?.files?.[0];
    if (!file) {
      toast.error("Bitte eine Datei wählen");
      return;
    }
    const fd = new FormData();
    fd.append("file", file);
    fd.append("employee_id", userId);
    fd.append("category", category);
    fd.append("description", desc);
    setBusy(true);
    try {
      await api.post("/documents/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Dokument hochgeladen");
      setDesc("");
      if (fileRef.current) fileRef.current.value = "";
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Upload fehlgeschlagen");
    } finally {
      setBusy(false);
    }
  };

  const download = async (d) => {
    try {
      const res = await api.get(`/documents/${d.id}/download`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data], { type: d.content_type }));
      const a = document.createElement("a");
      a.href = url; a.download = d.filename; document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Download fehlgeschlagen");
    }
  };

  const preview = async (d) => {
    try {
      const res = await api.get(`/documents/${d.id}/download`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data], { type: d.content_type }));
      window.open(url, "_blank");
    } catch {
      toast.error("Vorschau fehlgeschlagen");
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Dokument wirklich löschen?")) return;
    try {
      await api.delete(`/documents/${id}`);
      toast.success("Gelöscht");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Fehler");
    }
  };

  const filtered = filterCat === "all" ? docs : docs.filter((d) => d.category === filterCat);
  const grouped = CATEGORIES.reduce((acc, c) => { acc[c] = filtered.filter((d) => d.category === c); return acc; }, {});

  return (
    <div className="space-y-4">
      {canUpload && (
        <div className="ph-card p-5 md:p-6">
          <div className="flex items-center gap-2 mb-4">
            <Upload className="h-4 w-4 text-kauz-navy" />
            <h3 className="font-display text-base font-semibold">Datei hochladen</h3>
          </div>
          <div className="grid md:grid-cols-12 gap-3">
            <div className="md:col-span-4">
              <Label className="text-xs uppercase font-semibold text-slate-600 mb-1.5 block">Datei</Label>
              <Input ref={fileRef} type="file" data-testid="doc-file" accept=".pdf,.png,.jpg,.jpeg,.webp,.docx,.txt,.csv" />
            </div>
            <div className="md:col-span-3">
              <Label className="text-xs uppercase font-semibold text-slate-600 mb-1.5 block">Kategorie</Label>
              <Select value={category} onValueChange={setCategory}>
                <SelectTrigger data-testid="doc-category"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="md:col-span-3">
              <Label className="text-xs uppercase font-semibold text-slate-600 mb-1.5 block">Beschreibung</Label>
              <Input value={desc} onChange={(e) => setDesc(e.target.value)} placeholder="optional" />
            </div>
            <div className="md:col-span-2 flex items-end">
              <Button data-testid="doc-upload-btn" onClick={upload} disabled={busy} className="bg-sky-700 hover:bg-sky-800 w-full h-10">
                {busy ? "Lade hoch…" : "Hochladen"}
              </Button>
            </div>
          </div>
          <p className="text-[11px] text-slate-400 mt-3">
            Max. 25 MB. Sichtbar nur für dich und den Chef/Admin.
          </p>
        </div>
      )}

      <div className="flex items-center gap-2">
        <Filter className="h-4 w-4 text-slate-500" />
        <Select value={filterCat} onValueChange={setFilterCat}>
          <SelectTrigger className="w-48 h-9"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Alle Kategorien</SelectItem>
            {CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
          </SelectContent>
        </Select>
        <span className="text-xs text-slate-500 ml-auto">{filtered.length} Dokument{filtered.length === 1 ? "" : "e"}</span>
      </div>

      {filtered.length === 0 ? (
        <div className="ph-card p-12 text-center text-slate-400 text-sm">
          <FileText className="h-8 w-8 mx-auto mb-2 text-slate-300" />
          Keine Dokumente vorhanden.
        </div>
      ) : (
        CATEGORIES.filter((c) => grouped[c].length > 0).map((c) => (
          <div key={c} className="ph-card overflow-hidden">
            <div className="px-5 py-3 border-b border-slate-200 bg-slate-50/50 flex items-center justify-between">
              <h4 className="font-medium text-slate-700">{c}</h4>
              <Badge variant="outline" className="border-slate-200 text-slate-600">{grouped[c].length}</Badge>
            </div>
            <ul className="divide-y divide-slate-100">
              {grouped[c].map((d) => (
                <li key={d.id} className="px-5 py-3 flex items-center gap-3">
                  <div className="h-9 w-9 rounded-md bg-slate-50 border border-slate-200 grid place-items-center">
                    <FileText className="h-4 w-4 text-slate-500" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <button onClick={() => preview(d)} className="text-sm font-medium text-slate-900 truncate hover:text-kauz-navy block text-left w-full">
                      {d.filename}
                    </button>
                    <div className="text-xs text-slate-500 truncate">
                      {fmtSize(d.size)} · {new Date(d.created_at).toLocaleDateString("de-DE")} · {d.uploaded_by_name}
                      {d.description && ` · ${d.description}`}
                    </div>
                  </div>
                  <Button variant="outline" size="sm" className="h-8 w-8 p-0" onClick={() => download(d)} data-testid={`doc-dl-${d.id}`}>
                    <Download className="h-4 w-4" />
                  </Button>
                  {canDelete && (
                    <Button variant="outline" size="sm" className="h-8 w-8 p-0 text-red-600 hover:text-red-700" onClick={() => remove(d.id)}>
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  )}
                </li>
              ))}
            </ul>
          </div>
        ))
      )}
    </div>
  );
}
